import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { db } from '../firebase';
import { CAME_VIA_OPTIONS, correctActiveVisitSource } from '../utils/reception';
import type { ReceptionVisit } from '../utils/reception';

type BrokerOption = { id: string; name: string; agencyName: string; label: string };
type Source = 'direct' | 'broker' | 'c2c' | '';

const GOLD = '#9d6f32';
const LINE = '#d9d3c8';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export default function ReceptionSourceEditor({ visit, byEmail, onClose }: {
  visit: ReceptionVisit; byEmail: string; onClose: () => void;
}) {
  const current = visit.sourceCorrection?.to || {};
  const currentType = String(current.sourceType || (visit.cameVia === 'walk-in' ? 'direct' : visit.cameVia || ''));
  const [source, setSource] = useState<Source>(['direct', 'broker', 'c2c'].includes(currentType) ? currentType as Source : '');
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [broker, setBroker] = useState<BrokerOption | null>(() => {
    const id = String(current.brokerId || (visit.cameVia === 'broker' ? visit.brokerId || '' : ''));
    const name = String(current.brokerName || (visit.cameVia === 'broker' ? visit.brokerName || '' : ''));
    const agencyName = String(current.brokerAgencyName || (visit.cameVia === 'broker' ? visit.agencyName || '' : ''));
    return id || name ? { id, name, agencyName, label: [agencyName, name].filter(Boolean).join(' · ') } : null;
  });
  const [referrerName, setReferrerName] = useState(String(current.customerReferrerName || (visit.cameVia === 'c2c' ? visit.c2cName || '' : '')));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getDocs(query(collection(db, 'brokers'), limit(500))).then((snap) => {
      if (!active) return;
      setBrokers(snap.docs.map((row) => {
        const data = row.data();
        const name = String(data.brokerName || data.displayName || '').trim();
        const agencyName = String(data.agencyName || '').trim();
        return { id: row.id, name, agencyName, label: [agencyName, name].filter(Boolean).join(' · ') || row.id };
      }).filter((item) => !!item.name || !!item.agencyName));
    }).catch(() => { if (active) setBrokers([]); });
    return () => { active = false; };
  }, []);

  const save = async () => {
    if (!source) return setError('Choose Walk-in, Broker, or C2C.');
    if (source === 'broker' && (!broker?.id || (!broker.name && !broker.agencyName))) return setError('Choose a broker.');
    if (source === 'c2c' && !referrerName.trim()) return setError('Enter the C2C reference name.');
    setBusy(true);
    setError('');
    try {
      await correctActiveVisitSource(db, visit, {
        sourceType: source,
        brokerId: source === 'broker' ? broker?.id : null,
        brokerName: source === 'broker' ? broker?.name : null,
        brokerAgencyName: source === 'broker' ? broker?.agencyName : null,
        customerReferrerName: source === 'c2c' ? referrerName.trim() : null,
      }, byEmail);
      onClose();
    } catch (cause: any) {
      setError(cause?.message || 'Could not update the visit source.');
    } finally {
      setBusy(false);
    }
  };

  const options = broker && !brokers.some((item) => item.id === broker.id) ? [broker, ...brokers] : brokers;
  const pillSx = (selected: boolean) => ({
    minHeight: 36, px: 1.5, borderRadius: 999, border: selected ? 'none' : `1px solid ${LINE}`,
    bgcolor: selected ? GOLD : '#fff', color: selected ? '#fff' : '#1a1a1a',
    fontFamily: FONT, fontSize: 12.5, fontWeight: selected ? 700 : 500, cursor: 'pointer',
  });

  return <Box sx={{ p: 1.25, border: `1px solid ${LINE}`, borderRadius: 2, bgcolor: '#faf8f4', display: 'grid', gap: 1.25 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700 }}>Correct visit source</Typography>
    <Box role="group" aria-label="Visit source" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {CAME_VIA_OPTIONS.map((option) => {
        const value: Source = option.key === 'walk-in' ? 'direct' : option.key;
        return <Box key={option.key} component="button" type="button" aria-pressed={source === value}
          onClick={() => setSource(value)} sx={pillSx(source === value)}>{option.label}</Box>;
      })}
    </Box>
    {source === 'broker' && <Autocomplete<BrokerOption, false, false, false>
      options={options} value={broker} onChange={(_, value) => setBroker(value)}
      getOptionLabel={(option) => option.label} isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText="No brokers available"
      renderInput={(params) => <TextField {...params} size="small" label="Broker *" placeholder="Search broker or agency" />}
    />}
    {source === 'c2c' && <TextField size="small" fullWidth label="C2C reference name *"
      value={referrerName} onChange={(event) => setReferrerName(event.target.value)} />}
    {error && <Typography role="alert" sx={{ fontFamily: FONT, fontSize: 12, color: '#b3261e' }}>{error}</Typography>}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
      <Box component="button" type="button" onClick={onClose} disabled={busy} sx={pillSx(false)}>Cancel</Box>
      <Box component="button" type="button" onClick={save} disabled={busy}
        sx={{ ...pillSx(true), opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Save source'}</Box>
    </Box>
  </Box>;
}
